import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { createPluginRegistration } from '@embedpdf/core';
import { EmbedPDF } from '@embedpdf/core/react';
import {
  DocumentManagerPluginPackage,
  DocumentContent,
} from '@embedpdf/plugin-document-manager/react';
import {
  ViewportPluginPackage,
  Viewport,
} from '@embedpdf/plugin-viewport/react';
import { ScrollPluginPackage, Scroller } from '@embedpdf/plugin-scroll/react';
import {
  RenderPluginPackage,
  RenderLayer,
} from '@embedpdf/plugin-render/react';
import {
  ZoomPluginPackage,
  ZoomMode,
  useZoom,
} from '@embedpdf/plugin-zoom/react';
import { ThumbnailPluginPackage } from '@embedpdf/plugin-thumbnail/react';
import { usePdfEngine } from '@/features/pdf-engine';
import HeartLoader from '@/shared/ui/HeartLoader';
import { PdfThumbnailSidebar } from './PdfThumbnailSidebar';
import { attachZoomShortcuts } from './zoomShortcuts';
import styles from './PdfPreviewPane.module.css';

const PREPARING_VIEWER = 'Preparing viewer…';

interface PdfPreviewPaneProps {
  pdfBlob: Blob | null;
  isLoading: boolean;
  /** True while a refetch is in flight; the previous page stays visible. */
  isFetching?: boolean;
  error: unknown;
  showThumbnails?: boolean;
}

export default function PdfPreviewPane({
  pdfBlob,
  isLoading,
  isFetching = false,
  error,
  showThumbnails = false,
}: PdfPreviewPaneProps) {
  const {
    engine,
    isLoading: engineLoading,
    error: engineError,
    warmUp,
  } = usePdfEngine();
  const pdfBuffer = usePdfBuffer(pdfBlob);
  const viewportRef = useRef<HTMLDivElement | null>(null);

  // Covers landing here directly by URL, when nothing warmed the engine first.
  useEffect(() => {
    warmUp();
  }, [warmUp]);

  const plugins = useMemo(() => {
    if (!pdfBuffer) return null;
    return [
      createPluginRegistration(DocumentManagerPluginPackage, {
        initialDocuments: [{ buffer: pdfBuffer, name: 'report.pdf' }],
      }),
      createPluginRegistration(ViewportPluginPackage),
      createPluginRegistration(ScrollPluginPackage, { defaultPageGap: 4 }),
      createPluginRegistration(RenderPluginPackage),
      createPluginRegistration(ZoomPluginPackage, {
        defaultZoomLevel: ZoomMode.FitPage,
      }),
      createPluginRegistration(ThumbnailPluginPackage, {
        width: 120,
        gap: 8,
      }),
    ];
  }, [pdfBuffer]);

  if (error || engineError) {
    return (
      <PdfPreviewPlaceholder>
        Failed to load PDF preview. Please try again.
      </PdfPreviewPlaceholder>
    );
  }

  // Every pre-render state must show something. `!engine || !plugins` used to
  // return null, leaving a blank pane between the blob arriving and the
  // document being ready.
  if (isLoading) {
    return <PdfPreviewPlaceholder>Generating report…</PdfPreviewPlaceholder>;
  }

  if (engineLoading || !engine || !plugins) {
    return <PdfPreviewPlaceholder>{PREPARING_VIEWER}</PdfPreviewPlaceholder>;
  }

  return (
    <div className={styles.container}>
      {isFetching && (
        <div className={styles.refreshBanner} role="status">
          Updating preview…
        </div>
      )}
      <EmbedPDF engine={engine} plugins={plugins}>
        {({ activeDocumentId }) =>
          activeDocumentId && (
            <DocumentContent documentId={activeDocumentId}>
              {({ isLoaded, documentState }) =>
                !isLoaded ? (
                  <PdfPreviewPlaceholder>
                    {PREPARING_VIEWER}
                  </PdfPreviewPlaceholder>
                ) : (
                  <div className={styles.viewerLayout}>
                    {showThumbnails &&
                      (documentState?.document?.pageCount ?? 0) > 1 && (
                        <PdfThumbnailSidebar documentId={activeDocumentId} />
                      )}
                    <div className={styles.viewportWrap} ref={viewportRef}>
                      <Viewport
                        documentId={activeDocumentId}
                        className={styles.viewport}
                      >
                        <Scroller
                          documentId={activeDocumentId}
                          renderPage={({ width, height, pageIndex }) => (
                            <div style={{ width, height }}>
                              <RenderLayer
                                documentId={activeDocumentId}
                                pageIndex={pageIndex}
                              />
                            </div>
                          )}
                        />
                      </Viewport>
                      <ZoomToolbar documentId={activeDocumentId} />
                      <ZoomShortcuts
                        documentId={activeDocumentId}
                        target={viewportRef}
                      />
                    </div>
                  </div>
                )
              }
            </DocumentContent>
          )
        }
      </EmbedPDF>
    </div>
  );
}

/** Browser zoom gestures over the PDF zoom the PDF instead. */
function ZoomShortcuts({
  documentId,
  target,
}: {
  documentId: string;
  target: RefObject<HTMLDivElement | null>;
}) {
  const { provides } = useZoom(documentId);
  useEffect(() => {
    if (!provides || !target.current) return undefined;
    return attachZoomShortcuts(target.current, {
      zoomIn: () => provides.zoomIn(),
      zoomOut: () => provides.zoomOut(),
      fitPage: () => provides.requestZoom(ZoomMode.FitPage),
      zoomBy: (delta, { x, y }) =>
        provides.requestZoomBy(delta, { vx: x, vy: y }),
    });
  }, [provides, target]);
  return null;
}

function ZoomToolbar({ documentId }: { documentId: string }) {
  const { state, provides } = useZoom(documentId);
  const zoomPercent = Math.round(state.currentZoomLevel * 100);

  const handleZoomIn = useCallback(() => provides?.zoomIn(), [provides]);
  const handleZoomOut = useCallback(() => provides?.zoomOut(), [provides]);
  const handleFitPage = useCallback(
    () => provides?.requestZoom(ZoomMode.FitPage),
    [provides]
  );
  const handleFitWidth = useCallback(
    () => provides?.requestZoom(ZoomMode.FitWidth),
    [provides]
  );

  return (
    <div className={styles.zoomToolbar}>
      <button type="button" className={styles.zoomBtn} onClick={handleZoomOut}>
        &minus;
      </button>
      <span className={styles.zoomLevel}>{zoomPercent}%</span>
      <button type="button" className={styles.zoomBtn} onClick={handleZoomIn}>
        +
      </button>
      <button
        type="button"
        className={styles.zoomBtn}
        onClick={handleFitPage}
        title="Fit Page"
      >
        Page
      </button>
      <button
        type="button"
        className={styles.zoomBtn}
        onClick={handleFitWidth}
        title="Fit Width"
      >
        Width
      </button>
    </div>
  );
}

/**
 * Page-shaped placeholder. Keeping the same footprint as a rendered page means
 * the layout does not jump when the real document arrives.
 */
function PdfPreviewPlaceholder({ children }: { children: ReactNode }) {
  return (
    <div className={styles.container}>
      <div className={styles.centered}>
        <div className={styles.pageSkeleton}>
          <HeartLoader />
          <span className={styles.muted}>{children}</span>
        </div>
      </div>
    </div>
  );
}

/** Converts a Blob to ArrayBuffer asynchronously, memoized by blob reference. */
function usePdfBuffer(blob: Blob | null): ArrayBuffer | null {
  const [buffer, setBuffer] = useState<ArrayBuffer | null>(null);

  useEffect(() => {
    if (!blob) {
      setBuffer(null);
      return undefined;
    }
    let cancelled = false;
    blob.arrayBuffer().then((buf) => {
      if (!cancelled) setBuffer(buf);
    });
    return () => {
      cancelled = true;
    };
  }, [blob]);

  return buffer;
}
